import { Injectable } from '@angular/core';
import { getIDType } from '@datorama/akita';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AbstractFeatureService } from '@y42/feature';
import { METADATA_JSON_SCHEMA_URL, Y42MetadataGitModel } from '@y42/feature/json-schema';
import { AddEntityDialogResponse } from '@y42/feature/shared/add-entity';
import { ImportTemplateNotifierService, TemplateFile } from '@y42/feature/shared/import-export-template';
import { GitQuery } from '@y42/git';
import {
  DynamicOrchestrationConfig,
  OrchestrationGitLink,
  OrchestrationGitNode,
  OrchestrationType,
  StaticOrchestrationConfig,
  Y42FeatureType,
} from '@y42/json-schema';
import { ToastService } from '@y42/toast';
import { takePromise } from '@y42/util/take-promise';
import pick from 'lodash/pick';
import { Subject, tap } from 'rxjs';
import {
  OrchestrationGitService,
  OrchestrationQuery,
  OrchestrationRunStore,
  OrchestrationState,
  OrchestrationStore,
  OrchestrationStoreModel,
} from '../../state';
import { OrchestrationRunService } from '../orchestration-run';
import { OrchestrationAclService } from './orchestration-acl.service';
import { OrchestrationUserDataService } from './orchestration-user-data.service';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class OrchestrationService extends AbstractFeatureService<OrchestrationState> {
  constructor(
    protected readonly userData: OrchestrationUserDataService,
    protected readonly store: OrchestrationStore,
    protected readonly query: OrchestrationQuery,
    protected readonly git: OrchestrationGitService,
    protected readonly acl: OrchestrationAclService,
    protected readonly runStore: OrchestrationRunStore,
    protected readonly toast: ToastService,
    protected readonly gitQuery: GitQuery,
    private readonly importTemplateNotifierService: ImportTemplateNotifierService,
    private readonly orchestrationRunService: OrchestrationRunService,
  ) {
    super({
      featureType: Y42FeatureType.Orchestration,
      git,
      acl,
      store,
      query,
      userData,
      gitQuery,
      toast,
      childStores: [runStore],
    });

    // Create an orchestration on a template import.
    // TODO: This logic should be reverted. It is up to the import-export-template library to create the orchestration.
    // Right now it's impossible to import the orchestration service from the import-export-template library.
    this.createOrchestrationOnTemplateImport();
  }

  private readonly orchestrationFromImportCreated$$ = new Subject<TemplateFile>();
  public readonly orchestrationFromImportCreated$ = this.orchestrationFromImportCreated$$.pipe(untilDestroyed(this));

  override async getAll(options?: { setLoading?: boolean }): Promise<void> {
    if (options?.setLoading) {
      this.store.setLoading(true);
    }

    await super.getAll({ setLoading: false });

    const orchestrationPaths = this.query.getAll().map(({ $path }) => $path);
    if (orchestrationPaths.length) {
      await takePromise(this.orchestrationRunService.getScheduledEntitiesRuns(orchestrationPaths, 10));
    }

    if (options?.setLoading) {
      this.store.setLoading(false);
    }
  }

  async create({ title, description }: OrchestrationCreateConfig) {
    const { name, id } = this.query.getUniqueIdNamePair(title);

    const orchestration = this.store.create({
      ...new Y42MetadataGitModel(),
      $schema: METADATA_JSON_SCHEMA_URL,
      $name: id,
      name,
      description: description,
      type: OrchestrationType.Static,
      enabled: false,
      config: {
        linkDataArray: [],
        nodeDataArray: [],
        visualDataArray: [],
      },
      cronExpression: '',
      forceSchedule: false,
    });
    await this.store.pending;

    await this.git.commitChanges(`orchestrations: new orchestration "${name}" created`, orchestration.$path);

    return orchestration;
  }

  async createFull(data: {
    name: OrchestrationStoreModel['name'];
    type: OrchestrationStoreModel['type'];
    enabled: OrchestrationStoreModel['enabled'];
    config: OrchestrationStoreModel['config'];
    cronExpression: OrchestrationStoreModel['cronExpression'];
    forceSchedule: OrchestrationStoreModel['forceSchedule'];
    executionTimeout?: OrchestrationStoreModel['executionTimeout'];
    retryConfig?: OrchestrationStoreModel['retryConfig'];
  }) {
    const { name, id } = this.query.getUniqueIdNamePair(data.name);

    const orchestration = this.store.create({
      ...new Y42MetadataGitModel(),
      ...data,
      $schema: METADATA_JSON_SCHEMA_URL,
      $name: id,
      name,
      config: this.sanitizeConfig(data.config),
    });
    await this.store.pending;

    await this.git.commitChanges(`orchestrations: new orchestration "${name}" created`, orchestration.$path);

    return orchestration;
  }

  async update(item: Partial<OrchestrationStoreModel>, $path: OrchestrationStoreModel['$path']): Promise<void> {
    if (!$path) {
      throw new Error(`Orchestration "${$path}" not found.`);
    }

    if (item.config) {
      item.config = this.sanitizeConfig(item.config);
    }

    this.store.update($path, item);
    await this.store.pending;
  }

  async delete($path: string = this.query.getActiveId() as getIDType<OrchestrationState>) {
    const entity = this.query.getEntity($path);

    this.store.delete($path);
    await this.store.pending;
    await this.git.commitChanges(`orchestrations: orchestration '${entity?.$name ?? $path}' deleted`, [$path]);
  }

  async updateSettings(model: OrchestrationStoreModel, commitMessage: string) {
    await this.update(model, model.$path);
    const changedFiles = this.gitQuery.getModifiedFilesList().filter((filePath) => filePath.startsWith(model.$path));
    if (changedFiles.length > 0) {
      await this.git.commitChanges(`orchestration ${model.$name}: ${commitMessage}`, changedFiles);
    }
  }

  private sanitizeConfig(config: OrchestrationStoreModel['config']): OrchestrationStoreModel['config'] {
    const staticConfig = config as StaticOrchestrationConfig;
    if (staticConfig.nodeDataArray !== undefined) {
      const linkProps: Array<keyof OrchestrationGitLink> = ['from', 'to'];
      const nodeProps: Array<keyof OrchestrationGitNode> = [
        'key',
        'path',
        'group',
        'location',
        'onFailure',
        'nodeConfig',
      ];

      return {
        ...config,
        linkDataArray: staticConfig.linkDataArray.map((link) => pick(link, linkProps)),
        nodeDataArray: staticConfig.nodeDataArray.map((node) => pick(node, nodeProps)),
      };
    }
    return config;
  }

  private createOrchestrationOnTemplateImport() {
    this.importTemplateNotifierService.templateImported$
      .pipe(
        tap(async (templateDetails) => {
          if (!templateDetails.integrations.length) {
            return;
          }

          const config: DynamicOrchestrationConfig = {
            assets: templateDetails.integrations,
            dependencies: {
              downstream: true,
              upstream: true,
            },
            incremental: false,
            excludes: {
              exports: false,
              integrations: false,
            },
          };

          await this.createFull({
            name: `${templateDetails.title} import`,
            type: OrchestrationType.Dynamic,
            enabled: false,
            config,
            cronExpression: '',
            forceSchedule: false,
          });
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }
}

type OrchestrationCreateConfig = AddEntityDialogResponse;
